import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, X, CalendarIcon, ChevronDown, Search } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { FilterRule, FilterField, FilterOperator, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';

// Reusable searchable checkbox list for select-type filter options
const SelectSearchPopover = ({
  options,
  selectedValues,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (newValues: string[]) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const filtered = searchTerm
    ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  return (
    <div className="space-y-2">
      {options.length > 6 && (
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8 pl-7 text-sm"
          />
        </div>
      )}
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground px-1 py-2">No results</p>
        )}
        {filtered.map(option => {
          const isSelected = selectedValues.includes(option.value);
          return (
            <div
              key={option.value}
              className="flex items-center space-x-2 cursor-pointer hover:bg-accent p-1 rounded"
              onClick={() => {
                const newValues = isSelected
                  ? selectedValues.filter(v => v !== option.value)
                  : [...selectedValues, option.value];
                onChange(newValues);
              }}
            >
              <div className={`w-4 h-4 border rounded ${isSelected ? 'bg-primary border-primary' : 'border-input'}`}>
                {isSelected && <div className="w-2 h-2 bg-primary-foreground rounded-sm m-0.5"></div>}
              </div>
              <span className="text-sm">{option.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface FilterBuilderProps {
  draftGroups: FilterGroup[];
  appliedGroups: FilterGroup[];
  fields: FilterField[];
  entities: FilterEntity[];
  attributesByEntity: Record<string, FilterAttribute[]>;
  onAddGroup: () => void;
  onUpdateGroup: (groupId: string, updates: Partial<FilterGroup>) => void;
  onRemoveGroup: (groupId: string) => void;
  onAddRuleToGroup: (groupId: string) => void;
  onUpdateRuleInGroup: (groupId: string, ruleId: string, updates: Partial<FilterRule>) => void;
  onRemoveRuleFromGroup: (groupId: string, ruleId: string) => void;
  onClearAll: () => void;
  onApplyFilters: () => void;
  onRemoveAppliedRule?: (groupId: string, ruleId: string) => void;
  onRemoveAppliedGroup?: (groupId: string) => void;
  hasActiveFilters: boolean;
  hasUnappliedChanges: boolean;
  canApplyFilters: boolean;
}

const operatorsByType: Record<string, Array<{ value: FilterOperator; label: string }>> = {
  text: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
    { value: 'between', label: 'between' },
  ],
  date: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'after' },
    { value: 'less_than', label: 'before' },
    { value: 'between', label: 'between' },
  ],
  select: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
  ],
  boolean: [
    { value: 'equals', label: 'is' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
};

interface FilterRuleProps {
  rule: FilterRule;
  entities: FilterEntity[];
  attributesByEntity: Record<string, FilterAttribute[]>;
  onChange: (updates: Partial<FilterRule>) => void;
  onRemove: () => void;
  showLogic?: boolean;
}

function FilterRule({ 
  rule, 
  entities, 
  attributesByEntity, 
  onChange, 
  onRemove, 
  showLogic = false 
}: FilterRuleProps) {
  const currentEntity = entities.find(e => e.key === rule.entity);
  
  const getCurrentAttribute = () => {
    if (rule.entity && attributesByEntity[rule.entity]) {
      return attributesByEntity[rule.entity].find(attr => attr.key === rule.attribute);
    }
    return null;
  };

  const currentAttribute = getCurrentAttribute();
  const availableOperators = currentAttribute ? operatorsByType[currentAttribute.type] || [] : [];
  const availableAttributes = rule.entity ? (attributesByEntity[rule.entity] || []) : [];

  const renderValueInput = () => {
    if (!currentAttribute || !rule.operator) return null;

    const needsValue = !['is_empty', 'is_not_empty'].includes(rule.operator);
    if (!needsValue) return null;

    switch (currentAttribute.type) {
      case 'boolean':
        return (
          <Select
            value={rule.value?.toString() || ''}
            onValueChange={(value) => onChange({ value: value === 'true' })}
          >
            <SelectTrigger className="w-full sm:w-32">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent className="bg-background border shadow-md z-50">
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'text':
        return (
          <Input
            placeholder="Enter value..."
            value={rule.value || ''}
            onChange={(e) => onChange({ value: e.target.value })}
            className="w-full sm:w-48"
          />
        );

      case 'number':
        if (rule.operator === 'between') {
          return (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                type="number"
                placeholder="Min"
                value={rule.value?.min ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange({ value: { ...rule.value, min: val === '' ? '' : (parseFloat(val) || 0) } });
                }}
                onBlur={() => { if (rule.value?.min === '') onChange({ value: { ...rule.value, min: 0 } }); }}
                className="w-full sm:w-24"
              />
              <span className="text-muted-foreground">and</span>
              <Input
                type="number"
                placeholder="Max"
                value={rule.value?.max ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange({ value: { ...rule.value, max: val === '' ? '' : (parseFloat(val) || 0) } });
                }}
                onBlur={() => { if (rule.value?.max === '') onChange({ value: { ...rule.value, max: 0 } }); }}
                className="w-full sm:w-24"
              />
            </div>
          );
        }
        return (
          <Input
            type="number"
            placeholder="Enter amount..."
            value={rule.value ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ value: val === '' ? '' : (parseFloat(val) || 0) });
            }}
            onBlur={() => { if (rule.value === '') onChange({ value: 0 }); }}
            className="w-full sm:w-32"
          />
        );

      case 'date':
        if (rule.operator === 'between') {
          return (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-32 justify-start text-left font-normal">
                    <CalendarIcon className="h-4 w-4" />
                    {rule.value?.from ? format(rule.value.from, 'dd/MM/yyyy') : 'From'}
                  </Button>
                </PopoverTrigger>
                 <PopoverContent className="w-auto p-0 bg-background border shadow-md z-50" align="start">
                  <Calendar
                    mode="single"
                    selected={rule.value?.from}
                    onSelect={(date) => onChange({ 
                      value: { ...rule.value, from: date }
                    })}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-32 justify-start text-left font-normal">
                    <CalendarIcon className="h-4 w-4" />
                    {rule.value?.to ? format(rule.value.to, 'dd/MM/yyyy') : 'To'}
                  </Button>
                </PopoverTrigger>
                 <PopoverContent className="w-auto p-0 bg-background border shadow-md z-50" align="start">
                  <Calendar
                    mode="single"
                    selected={rule.value?.to}
                    onSelect={(date) => onChange({ 
                      value: { ...rule.value, to: date }
                    })}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          );
        }
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:w-48 justify-start text-left font-normal">
                <CalendarIcon className="h-4 w-4" />
                {rule.value ? format(rule.value, 'dd/MM/yyyy') : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-background border shadow-md z-50" align="start">
              <Calendar
                mode="single"
                selected={rule.value}
                onSelect={(date) => onChange({ value: date })}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        );

      case 'select':
        if (['in', 'not_in', 'equals', 'not_equals'].includes(rule.operator)) {
          const selectedValues = Array.isArray(rule.value) ? rule.value : [];
          return (
            <div className="w-full sm:w-48">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    {selectedValues.length > 0 
                      ? `${selectedValues.length} selected` 
                      : 'Select options...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2 bg-background border shadow-md z-50">
                  <SelectSearchPopover
                    options={currentAttribute.options || []}
                    selectedValues={selectedValues}
                    onChange={(newValues) => onChange({ value: newValues })}
                  />
                </PopoverContent>
              </Popover>
            </div>
          );
        }
        return (
          <Select
            value={rule.value || ''}
            onValueChange={(value) => onChange({ value })}
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Select value..." />
            </SelectTrigger>
            <SelectContent>
              {currentAttribute.options?.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      default:
        return null;
    }
  };

  return (
    <div className="relative grid grid-cols-2 sm:flex sm:items-center gap-2 p-3 pr-10 sm:pr-3 border rounded-lg bg-muted/50">
      {showLogic && (
        <Select
          value={rule.logic || 'AND'}
          onValueChange={(value: 'AND' | 'OR') => onChange({ logic: value })}
        >
          <SelectTrigger className="w-full sm:w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">AND</SelectItem>
            <SelectItem value="OR">OR</SelectItem>
          </SelectContent>
        </Select>
      )}

      <Select
        value={rule.entity || ''}
        onValueChange={(value) => onChange({ entity: value, attribute: '', operator: 'equals' as FilterOperator, value: null })}
      >
        <SelectTrigger className="w-full sm:w-32">
          <SelectValue placeholder="Entity..." />
        </SelectTrigger>
        <SelectContent className="bg-background border shadow-md z-50">
          {entities.map(entity => (
            <SelectItem key={entity.key} value={entity.key}>
              {entity.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={rule.attribute || ''}
        onValueChange={(value) => onChange({ attribute: value, operator: 'equals' as FilterOperator, value: null })}
        disabled={!rule.entity}
      >
        <SelectTrigger className="w-full sm:w-48">
          <SelectValue placeholder="Attribute..." />
        </SelectTrigger>
        <SelectContent className="bg-background border shadow-md z-50">
          {availableAttributes.map(attribute => (
            <SelectItem key={attribute.key} value={attribute.key}>
              {attribute.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={rule.operator || ''}
        onValueChange={(value: FilterOperator) => onChange({ operator: value, value: null })}
        disabled={!currentAttribute}
      >
        <SelectTrigger className="w-full sm:w-36">
          <SelectValue placeholder="Operator..." />
        </SelectTrigger>
        <SelectContent className="bg-background border shadow-md z-50">
          {availableOperators.map(op => (
            <SelectItem key={op.value} value={op.value}>
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="col-span-2 sm:col-span-1">{renderValueInput()}</div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="absolute top-2 right-2 sm:relative sm:top-auto sm:right-auto text-destructive hover:text-destructive h-7 w-7 p-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface FilterGroupProps {
  group: FilterGroup;
  entities: FilterEntity[];
  attributesByEntity: Record<string, FilterAttribute[]>;
  onUpdateGroup: (updates: Partial<FilterGroup>) => void;
  onRemoveGroup: () => void;
  onAddRule: () => void;
  onUpdateRule: (ruleId: string, updates: Partial<FilterRule>) => void;
  onRemoveRule: (ruleId: string) => void;
  showGroupLogic?: boolean;
}

function FilterGroup({ 
  group, 
  entities, 
  attributesByEntity,
  onUpdateGroup, 
  onRemoveGroup, 
  onAddRule, 
  onUpdateRule, 
  onRemoveRule, 
  showGroupLogic = false 
}: FilterGroupProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-3 sm:p-4 bg-muted/30">
        {/* Group Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <ChevronDown className={cn("h-4 w-4 transition-transform", !isOpen && "-rotate-90")} />
              </Button>
            </CollapsibleTrigger>
            {showGroupLogic && (
              <Select
                value={group.logic}
                onValueChange={(value: 'AND' | 'OR') => onUpdateGroup({ logic: value })}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
            )}
            <span className="text-sm font-medium text-muted-foreground">
              Filter Group {showGroupLogic ? '' : '(First Group)'}
            </span>
            {!isOpen && group.rules.length > 0 && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {group.rules.length} {group.rules.length === 1 ? 'filter' : 'filters'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onAddRule}>
              <Plus className="h-4 w-4" />
              Add Filter
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveGroup}
              className="text-destructive hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Group Rules */}
        <CollapsibleContent className="mt-3">
          <div className="space-y-2">
            {group.rules.map((rule, index) => (
              <FilterRule
                key={rule.id}
                rule={rule}
                entities={entities}
                attributesByEntity={attributesByEntity}
                onChange={(updates) => onUpdateRule(rule.id, updates)}
                onRemove={() => onRemoveRule(rule.id)}
                showLogic={index > 0}
              />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export function FilterBuilder({
  draftGroups,
  appliedGroups,
  fields,
  entities,
  attributesByEntity,
  onAddGroup,
  onUpdateGroup,
  onRemoveGroup,
  onAddRuleToGroup,
  onUpdateRuleInGroup,
  onRemoveRuleFromGroup,
  onClearAll,
  onApplyFilters,
  onRemoveAppliedRule,
  onRemoveAppliedGroup,
  hasActiveFilters,
  hasUnappliedChanges,
  canApplyFilters
}: FilterBuilderProps) {
  const getTotalRulesCount = (groups: FilterGroup[]) => {
    return groups.reduce((total, group) => total + group.rules.length, 0);
  };

  const getRuleDescription = (rule: FilterRule): string => {
    const field = fields.find(f => f.key === `${rule.entity}.${rule.attribute}`);
    const operator = operatorsByType[field?.type || 'text'].find(op => op.value === rule.operator);
    
    let valueStr = '';
    if (Array.isArray(rule.value)) {
      if (field?.options) {
        valueStr = rule.value.map(val => {
          const option = field.options?.find(opt => opt.value === val);
          return option?.label || val;
        }).join(', ');
      } else {
        valueStr = rule.value.join(', ');
      }
    } else {
      if (field?.options && rule.value) {
        const option = field.options.find(opt => opt.value === rule.value);
        valueStr = option?.label || rule.value.toString();
      } else {
        valueStr = rule.value?.toString() || '';
      }
    }
    
    return `${field?.label || rule.attribute} ${operator?.label || rule.operator} ${valueStr}`;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Add Filter Group Button */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onAddGroup}>
          <Plus className="h-4 w-4" />
          Add Filter Group
        </Button>
        {(draftGroups.length > 0 || appliedGroups.length > 0) && (
          <Button variant="ghost" size="sm" onClick={onClearAll}>
            Clear All ({Math.max(getTotalRulesCount(draftGroups), getTotalRulesCount(appliedGroups))})
          </Button>
        )}
      </div>

      {/* Draft Filter Groups */}
      {draftGroups.length > 0 && (
        <div className="space-y-4">
          {draftGroups.map((group, index) => (
            <FilterGroup
              key={group.id}
              group={group}
              entities={entities}
              attributesByEntity={attributesByEntity}
              onUpdateGroup={(updates) => onUpdateGroup(group.id, updates)}
              onRemoveGroup={() => onRemoveGroup(group.id)}
              onAddRule={() => onAddRuleToGroup(group.id)}
              onUpdateRule={(ruleId, updates) => onUpdateRuleInGroup(group.id, ruleId, updates)}
              onRemoveRule={(ruleId) => onRemoveRuleFromGroup(group.id, ruleId)}
              showGroupLogic={index > 0}
            />
          ))}
          
          {/* Apply Button near the filter groups */}
          {hasUnappliedChanges && (
            <div className="flex justify-end">
              <Button 
                variant="default" 
                size="sm" 
                onClick={onApplyFilters}
                disabled={!canApplyFilters}
              >
                Apply Filters
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Applied Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-6 p-3 sm:p-4 bg-muted/50 rounded-lg">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="font-medium text-sm sm:text-base">Applied Filters ({getTotalRulesCount(appliedGroups)} rules)</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAll}
              className="text-destructive hover:text-destructive"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          </div>
          <div className="space-y-2">
            {appliedGroups.map((group, groupIndex) => (
              <div key={group.id} className="text-sm">
                <div className="flex items-center gap-2 mb-1">
                  {group.rules.length > 1 && (
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      Group {groupIndex + 1} ({group.logic})
                    </span>
                  )}
                  {onRemoveAppliedGroup && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveAppliedGroup(group.id)}
                      className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {group.rules.map((rule, ruleIndex) => (
                    <div key={rule.id} className="flex items-center gap-1 min-w-0">
                      {groupIndex > 0 && ruleIndex === 0 && (
                        <span className="text-muted-foreground text-xs">
                          {group.logic}
                        </span>
                      )}
                      {ruleIndex > 0 && (
                        <span className="text-muted-foreground text-xs">
                          {rule.logic}
                        </span>
                      )}
                      <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded min-w-0">
                        <span className="text-xs truncate max-w-[200px] sm:max-w-none">
                          {getRuleDescription(rule)}
                        </span>
                        {onRemoveAppliedRule && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveAppliedRule(group.id, rule.id)}
                            className="h-4 w-4 p-0 flex-shrink-0 text-primary/60 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
